/**
 * When the hub renders a frame (the lobby's perf slice). The hub used to render every display frame, 60 a second
 * with the full post chain, for a menu whose camera now never moves — a phone ran warm sitting on it (ui.md F7).
 *
 * Now nothing is drawn unless there is a reason:
 * - `invalidate()`: something changed once (a re-frame, a ghost appearing, a resize) — the next frame renders;
 * - `burst(seconds)`: something is moving fast (a celebration, a build clip) — every display frame until it ends;
 * - otherwise the ambient loops (tower idle clips, chimney smoke, the flag, clouds) render at `AmbientFps`: 60 since
 *   2026-10-22 (owner: the 20 fps lobby looked choppy — the home screen must feel smooth), every other frame on a
 *   120 Hz display;
 * - `setPaused(true)`: a full-screen sheet covers the scene — nothing renders at all until it is resumed.
 *
 * Pure timing (timestamps in, decisions out) so it is unit-tested without a browser; HubScene owns the rAF.
 */
export class RenderScheduler {
  /** Frame rate of the ambient loops when nothing else asks for more. */
  static readonly AmbientFps = 60;
  /** Longest step handed to the animations (a tab that was hidden resumes without a jump). */
  static readonly MaxStep = 0.1;
  /**
   * An ambient frame is due this early (ms): rAF timestamps jitter by a millisecond or so, and a frame landing just
   * short of its slot would otherwise wait a whole display frame and drag the ambient rate down.
   */
  static readonly EarlyMs = 2;

  private paused = false;
  private dirty = true;
  private liveUntil = -Infinity;
  private last = -Infinity;
  /** When the next ambient frame is due (ms): slots a fixed period apart, so the rate holds at 60 Hz and 120 Hz alike. */
  private next = -Infinity;
  /** Frames rendered so far (the perf probe and the tests count them). */
  frames = 0;

  constructor(private readonly ambientFps = RenderScheduler.AmbientFps) {}

  /** Render the next frame (one-off change). */
  invalidate(): void { this.dirty = true; }

  /** Render every frame for `seconds` from `now` (ms), e.g. a one-shot clip; overlapping bursts extend it. */
  burst(seconds: number, now: number): void { this.liveUntil = Math.max(this.liveUntil, now + seconds * 1000); }

  /** Whether a burst is running at `now` (ms). */
  live(now: number): boolean { return now < this.liveUntil; }

  get isPaused(): boolean { return this.paused; }

  /** Stops (or resumes) rendering; a resume redraws at once, since whatever covered the scene may have changed it. */
  setPaused(on: boolean): void {
    if (this.paused === on) return;
    this.paused = on;
    if (!on) this.dirty = true;
  }

  /**
   * Called on every display frame with its timestamp (ms). Returns the animation step in seconds when this frame
   * should be rendered (the time since the last rendered one, capped), or null to skip it.
   */
  step(now: number): number | null {
    if (this.paused) return null;
    const period = 1000 / this.ambientFps;
    const ambient = now >= this.next - RenderScheduler.EarlyMs;
    if (!this.dirty && !this.live(now) && !ambient) return null;
    // the next slot follows this one's, or — after a pause, a burst or a stall — starts again from now
    this.next = ambient && now - this.next < period ? this.next + period : now + period;
    const since = now - this.last;
    this.dirty = false;
    this.last = now;
    this.frames++;
    return Number.isFinite(since) ? Math.min(RenderScheduler.MaxStep, Math.max(0, since / 1000)) : 0;
  }
}
